import {
  canonicalModuleCode,
  MODULE_CODES,
  type ModuleCode,
} from "../constants/modules";
import { PERMISSIONS, type Permission } from "../constants/permissions";
import type { Role } from "../constants/roles";
import type { PageOverrideMode } from "../access/types";
import {
  productRevivalCapabilities,
  type CapabilityCode,
} from "./capabilities";

export const pharmacySectionCodes = [
  "SELL",
  "PURCHASE",
  "STOCK",
  "PARTIES",
  "REPORTS",
  "SETTINGS",
] as const;

export type PharmacySectionCode = (typeof pharmacySectionCodes)[number];

export interface PharmacySectionDefinition {
  code: PharmacySectionCode;
  label: string;
  order: number;
}

export const pharmacySections: readonly PharmacySectionDefinition[] = [
  { code: "SELL", label: "Sell", order: 1 },
  { code: "PURCHASE", label: "Purchase", order: 2 },
  { code: "STOCK", label: "Stock", order: 3 },
  { code: "PARTIES", label: "Parties", order: 4 },
  { code: "REPORTS", label: "Reports", order: 5 },
  { code: "SETTINGS", label: "Settings", order: 6 },
] as const;

export type PharmacyRouteMatch = "exact" | "prefix";

export interface PharmacyRouteDefinition {
  id: string;
  section: PharmacySectionCode;
  label: string;
  path: string;
  match: PharmacyRouteMatch;
  icon: string;
  capability: CapabilityCode;
  legacyModules: readonly ModuleCode[];
  requiredModule?: ModuleCode;
  requiredPermission?: Permission;
  requiredRole?: Role;
  navigation: boolean;
  order: number;
}

const coreSales = [MODULE_CODES.SALES_MGMT] as const;
const corePurchase = [MODULE_CODES.PROCUREMENT] as const;
const coreStock = [MODULE_CODES.INVENTORY] as const;
const coreCustomers = [MODULE_CODES.CUSTOMER_MGMT] as const;
const coreSuppliers = [MODULE_CODES.SUPPLIER_MGMT] as const;
const coreReports = [MODULE_CODES.REPORTS] as const;
const coreSettings = [MODULE_CODES.SETTINGS] as const;

export const pharmacyRouteRegistry: readonly PharmacyRouteDefinition[] = [
  // Sell
  {
    id: "new-sale",
    section: "SELL",
    label: "New sale",
    path: "/pos",
    match: "prefix",
    icon: "ShoppingCart",
    capability: "CORE_PHARMACY",
    legacyModules: coreSales,
    requiredPermission: PERMISSIONS.BILLING_CREATE,
    navigation: true,
    order: 1,
  },
  {
    id: "bills",
    section: "SELL",
    label: "Bills",
    path: "/invoices",
    match: "prefix",
    icon: "FileText",
    capability: "CORE_PHARMACY",
    legacyModules: coreSales,
    requiredPermission: PERMISSIONS.BILLING_VIEW,
    navigation: true,
    order: 2,
  },
  {
    id: "new-return",
    section: "SELL",
    label: "Returns",
    path: "/sales-returns",
    match: "prefix",
    icon: "Undo2",
    capability: "CORE_PHARMACY",
    legacyModules: coreSales,
    requiredPermission: PERMISSIONS.BILLING_REFUND,
    navigation: true,
    order: 3,
  },
  {
    id: "approval-center",
    section: "SELL",
    label: "Approval Center",
    path: "/approvals",
    match: "exact",
    icon: "ClipboardCheck",
    capability: "CORE_PHARMACY",
    legacyModules: coreSales,
    requiredPermission: PERMISSIONS.APPROVALS_REVIEW,
    navigation: true,
    order: 4,
  },
  {
    id: "shifts",
    section: "SELL",
    label: "Shifts",
    path: "/billing-sessions",
    match: "prefix",
    icon: "Clock3",
    capability: "CORE_PHARMACY",
    legacyModules: coreSales,
    requiredPermission: PERMISSIONS.BILLING_VIEW,
    navigation: true,
    order: 5,
  },

  // Purchase
  {
    id: "receive-stock",
    section: "PURCHASE",
    label: "Receive stock",
    path: "/goods-receipts",
    match: "prefix",
    icon: "PackageCheck",
    capability: "CORE_PHARMACY",
    legacyModules: corePurchase,
    requiredPermission: PERMISSIONS.PROCUREMENT_VIEW,
    navigation: true,
    order: 1,
  },
  {
    id: "purchase-orders",
    section: "PURCHASE",
    label: "Purchase orders",
    path: "/purchase-orders",
    match: "prefix",
    icon: "ShoppingBag",
    capability: "CORE_PHARMACY",
    legacyModules: corePurchase,
    requiredPermission: PERMISSIONS.PURCHASE_ORDERS_VIEW,
    navigation: true,
    order: 2,
  },
  {
    id: "purchase-returns",
    section: "PURCHASE",
    label: "Purchase returns",
    path: "/purchase-returns",
    match: "prefix",
    icon: "ArrowRightLeft",
    capability: "CORE_PHARMACY",
    legacyModules: corePurchase,
    requiredPermission: PERMISSIONS.PROCUREMENT_VIEW,
    navigation: true,
    order: 3,
  },
  {
    id: "supplier-bills",
    section: "PURCHASE",
    label: "Supplier bills",
    path: "/supplier-bills",
    match: "prefix",
    icon: "Receipt",
    capability: "CORE_PHARMACY",
    legacyModules: corePurchase,
    requiredPermission: PERMISSIONS.PROCUREMENT_VIEW,
    navigation: true,
    order: 4,
  },

  // Stock
  {
    id: "products",
    section: "STOCK",
    label: "Products",
    path: "/products",
    match: "prefix",
    icon: "Pill",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.PRODUCTS_VIEW,
    navigation: true,
    order: 1,
  },
  {
    id: "stock",
    section: "STOCK",
    label: "Stock",
    path: "/inventory",
    match: "prefix",
    icon: "Boxes",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.INVENTORY_VIEW,
    navigation: true,
    order: 2,
  },
  {
    id: "batches",
    section: "STOCK",
    label: "Batches",
    path: "/batches",
    match: "prefix",
    icon: "ClipboardList",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.INVENTORY_VIEW,
    navigation: true,
    order: 3,
  },
  {
    id: "expiry",
    section: "STOCK",
    label: "Expiry",
    path: "/expiry-alerts",
    match: "prefix",
    icon: "CalendarClock",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.INVENTORY_VIEW,
    navigation: true,
    order: 4,
  },
  {
    id: "low-stock",
    section: "STOCK",
    label: "Low stock",
    path: "/reorder",
    match: "prefix",
    icon: "TriangleAlert",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.INVENTORY_VIEW,
    navigation: true,
    order: 5,
  },
  {
    id: "stock-count",
    section: "STOCK",
    label: "Stock count",
    path: "/physical-stock-counts",
    match: "prefix",
    icon: "ClipboardCheck",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.INVENTORY_ADJUST,
    navigation: true,
    order: 6,
  },
  {
    id: "stock-adjustments",
    section: "STOCK",
    label: "Adjustments",
    path: "/stock-adjustments",
    match: "prefix",
    icon: "SlidersHorizontal",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.INVENTORY_ADJUST,
    navigation: true,
    order: 7,
  },
  {
    id: "stock-transfers",
    section: "STOCK",
    label: "Transfers",
    path: "/stock-transfers",
    match: "prefix",
    icon: "ArrowRightLeft",
    capability: "MULTI_STORE",
    legacyModules: [MODULE_CODES.MULTI_STORE],
    requiredPermission: PERMISSIONS.INVENTORY_TRANSFER,
    navigation: true,
    order: 8,
  },

  // Parties
  {
    id: "customers",
    section: "PARTIES",
    label: "Customers",
    path: "/customers",
    match: "prefix",
    icon: "Users",
    capability: "CORE_PHARMACY",
    legacyModules: coreCustomers,
    requiredPermission: PERMISSIONS.CUSTOMERS_VIEW,
    navigation: true,
    order: 1,
  },
  {
    id: "suppliers",
    section: "PARTIES",
    label: "Suppliers",
    path: "/suppliers",
    match: "prefix",
    icon: "Truck",
    capability: "CORE_PHARMACY",
    legacyModules: coreSuppliers,
    requiredPermission: PERMISSIONS.SUPPLIERS_VIEW,
    navigation: true,
    order: 2,
  },

  // Reports
  {
    id: "reports",
    section: "REPORTS",
    label: "Reports",
    path: "/reports",
    match: "prefix",
    icon: "BarChart3",
    capability: "CORE_PHARMACY",
    legacyModules: coreReports,
    requiredPermission: PERMISSIONS.REPORTS_VIEW,
    navigation: true,
    order: 1,
  },

  // Settings
  {
    id: "settings",
    section: "SETTINGS",
    label: "Pharmacy settings",
    path: "/settings",
    match: "prefix",
    icon: "Settings",
    capability: "CORE_PHARMACY",
    legacyModules: coreSettings,
    requiredPermission: PERMISSIONS.SETTINGS_VIEW,
    navigation: true,
    order: 1,
  },
  {
    id: "import-center",
    section: "SETTINGS",
    label: "Import center",
    path: "/products/import",
    match: "exact",
    icon: "FileUp",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.PRODUCTS_IMPORT,
    navigation: true,
    order: 2,
  },
  {
    id: "staff",
    section: "SETTINGS",
    label: "Staff",
    path: "/users",
    match: "prefix",
    icon: "UserCog",
    capability: "CORE_PHARMACY",
    legacyModules: [MODULE_CODES.USER_MGMT],
    requiredPermission: PERMISSIONS.USERS_VIEW,
    navigation: true,
    order: 3,
  },
  {
    id: "counters",
    section: "SETTINGS",
    label: "Counters",
    path: "/counters",
    match: "prefix",
    icon: "LayoutGrid",
    capability: "CORE_PHARMACY",
    legacyModules: [MODULE_CODES.SALES_MGMT],
    requiredPermission: PERMISSIONS.SETTINGS_VIEW,
    navigation: true,
    order: 4,
  },
  {
    id: "stores",
    section: "SETTINGS",
    label: "Stores",
    path: "/stores",
    match: "prefix",
    icon: "Store",
    capability: "MULTI_STORE",
    legacyModules: [MODULE_CODES.MULTI_STORE],
    requiredPermission: PERMISSIONS.STORES_VIEW,
    navigation: true,
    order: 5,
  },
  {
    id: "subscription",
    section: "SETTINGS",
    label: "Subscription",
    path: "/subscription",
    match: "prefix",
    icon: "Crown",
    capability: "CORE_PHARMACY",
    legacyModules: [MODULE_CODES.ACCOUNT_BILLING],
    requiredModule: MODULE_CODES.ACCOUNT_BILLING,
    requiredPermission: PERMISSIONS.SUBSCRIPTION_MANAGE,
    navigation: true,
    order: 6,
  },
  {
    id: "schedule-h1",
    section: "SETTINGS",
    label: "Schedule H1",
    path: "/compliance/schedule-h1",
    match: "prefix",
    icon: "ShieldCheck",
    capability: "COMPLIANCE",
    legacyModules: [MODULE_CODES.COMPLIANCE],
    requiredPermission: PERMISSIONS.COMPLIANCE_VIEW,
    navigation: true,
    order: 7,
  },
  {
    id: "drug-licenses",
    section: "SETTINGS",
    label: "Drug licences",
    path: "/drug-licenses",
    match: "prefix",
    icon: "ScrollText",
    capability: "COMPLIANCE",
    legacyModules: [MODULE_CODES.DRUG_LICENSE],
    requiredModule: MODULE_CODES.DRUG_LICENSE,
    requiredPermission: PERMISSIONS.COMPLIANCE_VIEW,
    navigation: true,
    order: 8,
  },

  // More-specific write routes override their parent view route in direct URL checks.
  {
    id: "drug-license-create",
    section: "SETTINGS",
    label: "Create drug licence",
    path: "/drug-licenses/create",
    match: "exact",
    icon: "ScrollText",
    capability: "COMPLIANCE",
    legacyModules: [MODULE_CODES.DRUG_LICENSE],
    requiredModule: MODULE_CODES.DRUG_LICENSE,
    requiredPermission: PERMISSIONS.COMPLIANCE_MANAGE,
    navigation: false,
    order: 0,
  },
  {
    id: "drug-license-edit",
    section: "SETTINGS",
    label: "Edit drug licence",
    path: "/drug-licenses/:id/edit",
    match: "exact",
    icon: "ScrollText",
    capability: "COMPLIANCE",
    legacyModules: [MODULE_CODES.DRUG_LICENSE],
    requiredModule: MODULE_CODES.DRUG_LICENSE,
    requiredPermission: PERMISSIONS.COMPLIANCE_MANAGE,
    navigation: false,
    order: 0,
  },
  {
    id: "product-create",
    section: "STOCK",
    label: "Create product",
    path: "/products/new",
    match: "exact",
    icon: "Pill",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.PRODUCTS_CREATE,
    navigation: false,
    order: 0,
  },
  {
    id: "product-edit",
    section: "STOCK",
    label: "Edit product",
    path: "/products/:id/edit",
    match: "exact",
    icon: "Pill",
    capability: "CORE_PHARMACY",
    legacyModules: coreStock,
    requiredPermission: PERMISSIONS.PRODUCTS_UPDATE,
    navigation: false,
    order: 0,
  },
  {
    id: "customer-create",
    section: "PARTIES",
    label: "Create customer",
    path: "/customers/create",
    match: "exact",
    icon: "Users",
    capability: "CORE_PHARMACY",
    legacyModules: coreCustomers,
    requiredPermission: PERMISSIONS.CUSTOMERS_CREATE,
    navigation: false,
    order: 0,
  },
  {
    id: "customer-edit",
    section: "PARTIES",
    label: "Edit customer",
    path: "/customers/:id/edit",
    match: "exact",
    icon: "Users",
    capability: "CORE_PHARMACY",
    legacyModules: coreCustomers,
    requiredPermission: PERMISSIONS.CUSTOMERS_UPDATE,
    navigation: false,
    order: 0,
  },
  {
    id: "supplier-create",
    section: "PARTIES",
    label: "Create supplier",
    path: "/suppliers/create",
    match: "exact",
    icon: "Truck",
    capability: "CORE_PHARMACY",
    legacyModules: coreSuppliers,
    requiredPermission: PERMISSIONS.SUPPLIERS_CREATE,
    navigation: false,
    order: 0,
  },
  {
    id: "supplier-edit",
    section: "PARTIES",
    label: "Edit supplier",
    path: "/suppliers/:id/edit",
    match: "exact",
    icon: "Truck",
    capability: "CORE_PHARMACY",
    legacyModules: coreSuppliers,
    requiredPermission: PERMISSIONS.SUPPLIERS_UPDATE,
    navigation: false,
    order: 0,
  },
  {
    id: "user-create",
    section: "SETTINGS",
    label: "Create staff",
    path: "/users/create",
    match: "exact",
    icon: "UserCog",
    capability: "CORE_PHARMACY",
    legacyModules: [MODULE_CODES.USER_MGMT],
    requiredPermission: PERMISSIONS.USERS_CREATE,
    navigation: false,
    order: 0,
  },
  {
    id: "user-edit",
    section: "SETTINGS",
    label: "Edit staff",
    path: "/users/:id/edit",
    match: "exact",
    icon: "UserCog",
    capability: "CORE_PHARMACY",
    legacyModules: [MODULE_CODES.USER_MGMT],
    requiredPermission: PERMISSIONS.USERS_UPDATE,
    navigation: false,
    order: 0,
  },
  {
    id: "store-create",
    section: "SETTINGS",
    label: "Create store",
    path: "/stores/create",
    match: "exact",
    icon: "Store",
    capability: "MULTI_STORE",
    legacyModules: [MODULE_CODES.MULTI_STORE],
    requiredPermission: PERMISSIONS.STORES_CREATE,
    navigation: false,
    order: 0,
  },
  {
    id: "store-edit",
    section: "SETTINGS",
    label: "Edit store",
    path: "/stores/:id/edit",
    match: "exact",
    icon: "Store",
    capability: "MULTI_STORE",
    legacyModules: [MODULE_CODES.MULTI_STORE],
    requiredPermission: PERMISSIONS.STORES_UPDATE,
    navigation: false,
    order: 0,
  },
  {
    id: "dashboard",
    section: "SELL",
    label: "Dashboard",
    path: "/dashboard",
    match: "exact",
    icon: "LayoutDashboard",
    capability: "CORE_PHARMACY",
    legacyModules: [MODULE_CODES.DASHBOARD],
    navigation: true,
    order: 0,
  },
  {
    id: "profile",
    section: "SETTINGS",
    label: "Profile",
    path: "/profile",
    match: "exact",
    icon: "User",
    capability: "CORE_PHARMACY",
    legacyModules: coreSettings,
    navigation: false,
    order: 0,
  },
  {
    id: "notifications",
    section: "SETTINGS",
    label: "Notifications",
    path: "/notifications",
    match: "exact",
    icon: "Bell",
    capability: "CORE_PHARMACY",
    legacyModules: coreSettings,
    navigation: false,
    order: 0,
  },
] as const;

export interface PharmacyAccessContext {
  enabledCapabilities: readonly string[];
  roleName?: string | null;
  hasPermission: (permission: Permission) => boolean;
  hasRole: (role: Role) => boolean;
  pageOverrides?: Readonly<Partial<Record<string, PageOverrideMode>>>;
  disabledPageKeys?: readonly string[];
}

export type PharmacyAccessDenial =
  | "ROUTE_NOT_RELEASED"
  | "CAPABILITY_DISABLED"
  | "PERMISSION_DENIED"
  | "ROLE_DENIED"
  | "PAGE_HIDDEN"
  | "PAGE_DENIED";

export interface PharmacyRouteAccess {
  allowed: boolean;
  denial?: PharmacyAccessDenial;
  route?: PharmacyRouteDefinition;
}

function normalizePath(path: string): string {
  const normalized = path.split(/[?#]/, 1)[0].replace(/\/+$/, "");
  return normalized || "/";
}

function pathMatches(route: PharmacyRouteDefinition, pathname: string) {
  const actual = normalizePath(pathname).split("/").filter(Boolean);
  const pattern = normalizePath(route.path).split("/").filter(Boolean);
  if (route.match === "exact" && actual.length !== pattern.length) return false;
  if (route.match === "prefix" && actual.length < pattern.length) return false;
  return pattern.every(
    (part, index) => part.startsWith(":") || part === actual[index],
  );
}

function specificity(route: PharmacyRouteDefinition) {
  return (
    route.path.split("/").filter(Boolean).length * 10 +
    route.path.split("/").filter((part) => part && !part.startsWith(":")).length
  );
}

export function resolvePharmacyRoute(pathname: string) {
  return [...pharmacyRouteRegistry]
    .sort((left, right) => specificity(right) - specificity(left))
    .find((route) => pathMatches(route, pathname));
}

function capabilityEnabled(
  route: PharmacyRouteDefinition,
  enabledValues: readonly string[],
) {
  if (
    route.requiredModule &&
    !isModuleOrCapabilityEnabled(route.requiredModule, enabledValues)
  ) {
    return false;
  }
  const capability = productRevivalCapabilities[route.capability];
  if (!capability.releaseEnabled) return false;
  const enabled = new Set(enabledValues);
  if (enabled.has(route.capability)) {
    return capability.dependencies.every((dependency) =>
      enabled.has(dependency),
    );
  }
  return route.legacyModules.some((moduleCode) =>
    isModuleOrCapabilityEnabled(moduleCode, enabledValues),
  );
}

export function evaluatePharmacyRouteAccess(
  pathname: string,
  context: PharmacyAccessContext,
): PharmacyRouteAccess {
  const route = resolvePharmacyRoute(pathname);
  if (!route) return { allowed: false, denial: "ROUTE_NOT_RELEASED" };
  if (context.disabledPageKeys?.includes(route.id)) {
    return { allowed: false, denial: "ROUTE_NOT_RELEASED", route };
  }
  if (context.pageOverrides?.[route.id] === "DENIED") {
    return { allowed: false, denial: "PAGE_DENIED", route };
  }
  if (!capabilityEnabled(route, context.enabledCapabilities)) {
    return { allowed: false, denial: "CAPABILITY_DISABLED", route };
  }
  if (
    route.requiredPermission &&
    context.roleName !== "SUPER_ADMIN" &&
    !context.hasPermission(route.requiredPermission)
  ) {
    return { allowed: false, denial: "PERMISSION_DENIED", route };
  }
  if (route.requiredRole && !context.hasRole(route.requiredRole)) {
    return { allowed: false, denial: "ROLE_DENIED", route };
  }
  return { allowed: true, route };
}

export interface PharmacyNavigationGroup {
  code: PharmacySectionCode;
  title: string;
  items: PharmacyRouteDefinition[];
}

export function getPharmacyNavigationGroups(
  context: PharmacyAccessContext,
): PharmacyNavigationGroup[] {
  return pharmacySections
    .map((section) => ({
      code: section.code,
      title: section.label,
      items: pharmacyRouteRegistry
        .filter(
          (route) =>
            route.navigation &&
            route.section === section.code &&
            context.pageOverrides?.[route.id] !== "HIDDEN" &&
            evaluatePharmacyRouteAccess(route.path, context).allowed,
        )
        .sort((left, right) => left.order - right.order),
    }))
    .filter((section) => section.items.length > 0);
}

const moduleCapability: Partial<Record<ModuleCode, CapabilityCode>> = {
  [MODULE_CODES.SALES_MGMT]: "CORE_PHARMACY",
  [MODULE_CODES.INVENTORY]: "CORE_PHARMACY",
  [MODULE_CODES.CUSTOMER_MGMT]: "CORE_PHARMACY",
  [MODULE_CODES.REPORTS]: "CORE_PHARMACY",
  [MODULE_CODES.PROCUREMENT]: "CORE_PHARMACY",
  [MODULE_CODES.SUPPLIER_MGMT]: "CORE_PHARMACY",
  [MODULE_CODES.USER_MGMT]: "CORE_PHARMACY",
  [MODULE_CODES.SETTINGS]: "CORE_PHARMACY",
  [MODULE_CODES.ACCOUNT_BILLING]: "CORE_PHARMACY",
  [MODULE_CODES.COMPLIANCE]: "COMPLIANCE",
  [MODULE_CODES.MULTI_STORE]: "MULTI_STORE",
  [MODULE_CODES.LOYALTY_PROGRAM]: "GROWTH",
  [MODULE_CODES.DISCOUNT]: "GROWTH",
  [MODULE_CODES.DOCTOR_MGMT]: "GROWTH",
  [MODULE_CODES.HOSPITAL_MGMT]: "GROWTH",
  [MODULE_CODES.DRUG_GENERICS]: "GROWTH",
};

export function isModuleOrCapabilityEnabled(
  requiredModule: ModuleCode,
  enabledValues: readonly string[],
) {
  const normalizedEnabled = new Set(
    enabledValues.map((value) => canonicalModuleCode(value)),
  );
  if (normalizedEnabled.has(requiredModule)) return true;
  const capability = moduleCapability[requiredModule];
  if (!capability) return false;
  const definition = productRevivalCapabilities[capability];
  return definition.releaseEnabled && normalizedEnabled.has(capability);
}
